import { useSubscription, useSuspenseQuery } from '@apollo/client/react';
import { Spinner } from '@chakra-ui/react';
import { Suspense, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root';
import { GET_DOCUMENT_BY_PATH } from '#graphql/queries/document';
import { DOCUMENT_UPDATED } from '#graphql/subscriptions/document-updated';
import { useAppSettings } from '#providers/app-settings-provider';
import { getDocumentPathFromViewSplat } from '#utils/document-app-path';
import { isValidDocumentPath } from '#utils/document-path';

import { ViewDocument } from './index';

/**
 * Renders a single document view; splat is the document path (`/d/view/...` route).
 * Defined at module level to keep a stable tree identity and avoid re-mount loops.
 */
function ViewRouteWithData({ documentPath }: { documentPath: string }) {
  const { documentPathRegex } = useAppSettings();
  const { data, refetch: refetchDocument } = useSuspenseQuery(
    GET_DOCUMENT_BY_PATH,
    { variables: { path: documentPath } },
  );
  const result = data?.documentByPath;

  const lastServerEditTimestampRef = useRef(0);

  useSubscription(DOCUMENT_UPDATED, {
    variables: { path: documentPath },
    skip: !isValidDocumentPath(documentPath, documentPathRegex),
    onData: ({ data: subData }) => {
      const ts = subData.data?.documentUpdated?.editTimestamp;
      if (typeof ts !== 'number') return;

      if (ts <= lastServerEditTimestampRef.current) return;

      lastServerEditTimestampRef.current = ts;
      void refetchDocument();
    },
  });

  const path =
    result?.__typename === 'Document'
      ? result.path
      : result?.__typename === 'DocumentNotFound'
        ? (result.requestedPath ?? documentPath)
        : documentPath;

  return (
    <ViewDocument
      key={path}
      document={result}
      path={path}
      refetch={refetchDocument}
      lastServerEditTimestampRef={lastServerEditTimestampRef}
    />
  );
}

export function DocumentDViewRoute() {
  const params = useParams();
  const pathFromUrl = getDocumentPathFromViewSplat(
    (params as Record<string, string | undefined>)['*'],
  );

  const canRender = useMemo(() => pathFromUrl.length > 0, [pathFromUrl]);

  return canRender ? (
    <Suspense
      fallback={
        <PageContentRoot
          flex={1}
          alignSelf="stretch"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH={0}
        >
          <Spinner size="xl" />
        </PageContentRoot>
      }
    >
      <ViewRouteWithData documentPath={pathFromUrl} />
    </Suspense>
  ) : null;
}
